import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { ReactComponent as FigmaLogo } from 'assets/figma.svg';

import VideoUpload from 'Components/VideoUpload';

import { getFigmaConnected } from 'Redux/Actions/figmaActions';

import FigmaFile from 'services/figma_file';

import { errorReactToastify, infoReactToastify } from 'utlis/toasts';
import { isFigmaURL } from 'utlis/validators';

import { MAX_UPLOAD_FILES } from './constants';

import styles from './styles.module.scss';

const DesignSelection = ({
  setUploadedImages,
  setOriginalFiles,
  uploadingImages,
  figmaUrl,
  uploadedImages,
  setFigmaUrl,
  setFigmaFramesData,
  productDescription,
  isSubmitting,
  title,
  audience,
  businessGoal,
  selectedCategory,
  selectedProblemChips,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const currentRequestRef = useRef(null);

  const [error, setError] = useState('');
  const [isLoadingFigmaImages, setIsLoadingFigmaImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showVideoUpload, setShowVideoUpload] = useState(false);

  const userProfile = useSelector(state => state.userProfile);
  const authUser = useSelector(state => state.userStore);

  const handleVideoFramesExtracted = useCallback(videoFrames => {
    const mappedFrames = (videoFrames || []).map((frame, index) => ({
      name: `Frame ${index + 1}`,
      uid: `video-frame-${Date.now()}-${index}`,
      preview: frame.s3_url,
      url: frame.s3_url,
      progress: 100,
      isProgressLoading: false,
      isVideoFrame: true,
      timestamp: frame.timestamp_ms,
    }));

    setUploadedImages(mappedFrames);
    setShowVideoUpload(false);
  }, [setUploadedImages]);

  const handleVideoUploadCancel = useCallback(() => {
    setShowVideoUpload(false);
  }, []);

  const firstThreeImages = useMemo(() => uploadedImages ? uploadedImages?.slice(0, 3) : [], [uploadedImages]);

  const isLoading = useMemo(() => (
    (isLoadingFigmaImages && uploadedImages.length === 0)
      || uploadingImages
      || isSubmitting
  ), [isLoadingFigmaImages, uploadedImages, uploadingImages, isSubmitting]);

  const loadingMessage = useMemo(() => {
    if (uploadingImages) {
      return 'Uploading images...';
    }
    if (isLoadingFigmaImages && uploadedImages.length === 0) {
      return 'Loading your designs...';
    }
    if (isSubmitting) {
      return 'Submitting your review...';
    }

    return 'Loading...';
  }, [uploadingImages, isLoadingFigmaImages, uploadedImages, isSubmitting]);

  // Debounce effect for Figma URL changes
  useEffect(() => {
    if (!figmaUrl || error) {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchFigmaImages(figmaUrl);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      // Mark current request as stale when user types again
      currentRequestRef.current = null;
    };
  }, [figmaUrl, error]);

  // Drag and drop event handlers
  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();

    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);

    if (files.length > 0) {
      processFiles(files);
    }
  };

  return (
    <div
      className={cn('flex rounded-lg shadow-lg mx-8', styles.createReviewRightPanel, {
        'bg-gray-100': uploadedImages.length > 0,
        'bg-white': uploadedImages.length === 0,
      })}
      style={{
        boxShadow: '0px 4px 16px -2px rgba(0, 0, 0, 0.08), 0px 2px 4px 0px rgba(0, 0, 0, 0.04)',
      }}
    >
      <div
        className={
          cn(
            'flex flex-col items-center justify-center flex-1 px-8 text-center rounded-lg mx-8 my-8 gap-16',
            {
              'border-2 border-dashed border-gray-300': uploadedImages.length === 0,
              'bg-gray-50': !isDragging,
              'bg-gray-100': isDragging,
            }
          )
        }
        onDragEnter={uploadedImages.length === 0 ? handleDragEnter : undefined}
        onDragOver={uploadedImages.length === 0 ? handleDragOver : undefined}
        onDragLeave={uploadedImages.length === 0 ? handleDragLeave : undefined}
        onDrop={uploadedImages.length === 0 ? handleDrop : undefined}
      >
        {/* UI when no images or figma frame selected or uploaded */}
        {uploadedImages.length === 0 && !isLoadingFigmaImages && !uploadingImages && !showVideoUpload && (
          <>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={triggerFileInput}
              className="text-gray-800 text-sm font-normal cursor-pointer transition-colors
                w-full bg-white border border-gray-300 rounded-lg shadow-sm mx-auto h-[44px] max-w-[310px] px-4 py-3"
            >
              Upload up to 10 screenshots
            </button>
            <div className="text-gray-500 text-sm">
              OR
            </div>
            <button
              onClick={() => setShowVideoUpload(true)}
              className="text-gray-800 text-sm font-normal cursor-pointer transition-colors
                w-full bg-white border border-gray-300 rounded-lg shadow-sm mx-auto h-[44px] max-w-[310px] px-4 py-3"
            >
              Upload a video
            </button>
            <div className="text-gray-500 text-sm">
              OR
            </div>
            {userProfile?.appTokens?.figma
              ? (
                <div
                  className="w-full bg-white border border-gray-300 rounded-lg shadow-sm mx-auto h-11
                   px-16 py-12 max-w-[310px]"
                >
                  <div className="flex items-center gap-8 h-5">
                    <div className="flex-shrink-0">
                      <FigmaLogo alt="Figma" />
                    </div>
                    {/* Input */}
                    <input
                      type="text"
                      placeholder="Add link of a Figma section"
                      value={figmaUrl}
                      onChange={e => {
                        setFigmaUrl(e.target.value);

                        if (e.target.value && !isFigmaURL(e.target.value)) {
                          setError('Please enter valid figma url');
                        }
                        else {
                          setError('');
                        }
                      }}
                      className="flex-1 h-5 text-sm text-gray-600 placeholder-gray-500 bg-transparent border-none outline-none"
                    />
                  </div>
                </div>
              )
              : (
                <button
                  onClick={handleFigmaConnect}
                  className="flex items-center justify-center gap-2 w-full px-5 py-2.5
                  bg-[#0F99FF] text-white text-sm font-medium rounded-lg
                  shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.04),0px_1px_1px_rgba(0,0,0,0.02)]
                  hover:bg-[#0088E6] transition-colors mx-auto max-w-[310px] h-11"
                >
                  <FigmaLogo className="w-5 h-5" />
                  Connect Figma account
                </button>
              )}
            {error
              ? (
                <div className="text-red-500 text-sm h-4 mt-[-4px]">
                  {error}
                </div>
              )
              : (
                <div className="text-gray-500 text-sm h-4 mt-[-4px]">
                  {`Max. ${MAX_UPLOAD_FILES} frames`}
                </div>
              )}
          </>
        )}
        {/* Video upload flow */}
        {uploadedImages.length === 0 && !isLoadingFigmaImages && !uploadingImages && showVideoUpload && (
          <VideoUpload
            onFramesExtracted={handleVideoFramesExtracted}
            onCancel={handleVideoUploadCancel}
          />
        )}
        {isLoading && (
          <div className="flex flex-col items-center justify-center gap-24">
            <ImpulseSpinner frontColor={'#28BDAE'} />
            <p className="text-sm text-gray-500">
              {loadingMessage}
            </p>
          </div>
        )}
        {(uploadedImages.length > 0 && !uploadingImages && !isSubmitting) && (
          <div className="relative flex flex-col items-center justify-center w-full h-full">
            <p className="text-sm mb-10 text-gray-500">
              {`${uploadedImages.length} screen${uploadedImages.length === 1 ? '' : 's'} in your flow`}
            </p>
            <div className="flex flex-col items-center justify-center w-3/4 h-3/5 2xl:h-1/2 min-w-72">
              <div className={styles['image-container']}>
                {firstThreeImages.map((image, index) => (
                  <img
                    key={index}
                    src={image.preview}
                    alt={`Image ${index + 1}`}
                    className={`${styles['image']} ${styles[`rotate${index % 5}`]} ${styles[`zIndex${10 - index}`]}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  function handleFigmaConnect() {
    // Store TeamDashboard-specific fields in localStorage before redirect
    if (title && title.trim()) {
      localStorage.setItem('teamReview_title', title);
    }

    if (productDescription && productDescription.trim()) {
      localStorage.setItem('teamReview_productDescription', productDescription);
    }

    if (audience && audience.trim()) {
      localStorage.setItem('teamReview_audience', audience);
    }

    if (businessGoal && businessGoal.trim()) {
      localStorage.setItem('teamReview_businessGoal', businessGoal);
    }

    if (selectedCategory) {
      localStorage.setItem('teamReview_selectedCategory', selectedCategory);
    }

    if (selectedProblemChips && Array.isArray(selectedProblemChips) && selectedProblemChips.length > 0) {
      localStorage.setItem('teamReview_selectedProblemChips', JSON.stringify(selectedProblemChips));
    }

    // Store flag to reopen modal after Figma authentication
    localStorage.setItem('teamReview_shouldReopenModal', 'true');

    infoReactToastify(
      'Hang tight, we\'re connecting to your Figma account',
      { position: 'bottom-center', style: { backgroundColor: '#EEF8FF', width: '470px' } }
    );

    setTimeout(
      () => dispatch(getFigmaConnected(authUser, null, () => {}, 'team')),
      2000
    );
  }

  function processFiles(files) {
    if (uploadedImages.length + files.length > MAX_UPLOAD_FILES) {
      setError(`You can only upload up to ${MAX_UPLOAD_FILES} images`);

      return;
    }

    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      setError('Please upload valid image files');

      return;
    }

    const filesWithId = imageFiles.map(file => ({
      ...file,
      name: file.name,
      uid: Date.now() + Math.random(),
      // eslint-disable-next-line compat/compat
      preview: URL.createObjectURL(file),
      progress: 0,
      isProgressLoading: false,
      url: null,
    }));

    setUploadedImages(prev => [...prev, ...filesWithId]);
    setOriginalFiles(imageFiles);
    setError('');
  }

  function handleFileSelect(event) {
    const files = Array.from(event.target.files);

    processFiles(files);

    event.target.value = '';
  }

  function triggerFileInput() {
    setError('');
    fileInputRef.current?.click();
  }

  async function fetchFigmaImages(url) {
    if (!isFigmaURL(url)) {
      return;
    }

    // Create a unique request ID to track this specific request
    const requestId = Date.now();

    currentRequestRef.current = requestId;

    setIsLoadingFigmaImages(true);

    try {
      const response = await FigmaFile.getFigmaPageImages(url, null, authUser);

      // Check if this request is still the current one
      if (currentRequestRef.current !== requestId) {
        return; // Request was superseded, ignore response
      }

      if (response.data && response.data.status === 'success') {
        const data = response.data;
        const { image_urls = [], frame_count = 0 } = data;
        let frameList = Array.isArray(data.frames) && data.frames.length > 0
          ? data.frames
          : image_urls.map((imageUrl, i) => ({
            frame_id: `0:${i}`,
            frame_name: `Frame ${i + 1}`,
            image_url: imageUrl,
          }));

        if (frameList.length < frame_count && image_urls.length > 0) {
          const lastUrl = image_urls[image_urls.length - 1];

          while (frameList.length < frame_count) {
            frameList = frameList.concat({
              frame_id: `0:${frameList.length}`,
              frame_name: `Frame ${frameList.length + 1}`,
              image_url: lastUrl,
            });
          }
        }

        if (frameList.length > 0) {
          const figmaImages = frameList.map((frame, index) => {
            const imageUrl = frame.image_url || frame.imageUrl;

            return {
              name: frame.frame_name || `Figma Image ${index + 1}`,
              uid: `figma-${Date.now()}-${index}`,
              preview: imageUrl,
              url: imageUrl,
              progress: 100,
              isProgressLoading: false,
              isFigmaImage: true,
            };
          });

          setUploadedImages(figmaImages);

          setFigmaFramesData?.({
            frames: frameList,
            section_name: data.section_name ?? '',
            file_key: data.file_key ?? '',
            figma_url: url,
          });
        }
      }
    }
    catch (err) {
      // Only log errors if this request is still current
      if (currentRequestRef.current === requestId) {
        errorReactToastify(
          err?.response?.data?.message || 'Failed to fetch Figma images',
          {
            autoClose: 5000,
            position: 'bottom-center',
            hideProgressBar: true,
            style: { backgroundColor: '#FDF0F0', width: '470px', height: '56px' },
          }
        );

        if (err?.response?.data?.message) {
          setFigmaUrl('');
        }
      }
    }
    finally {
      // Only update loading state if this request is still current
      if (currentRequestRef.current === requestId) {
        setIsLoadingFigmaImages(false);
      }
    }
  }
};

DesignSelection.propTypes = {
  setUploadedImages: PropTypes.func.isRequired,
  setOriginalFiles: PropTypes.func.isRequired,
  uploadingImages: PropTypes.bool.isRequired,
  figmaUrl: PropTypes.string.isRequired,
  uploadedImages: PropTypes.object.isRequired,
  setFigmaUrl: PropTypes.func.isRequired,
  setFigmaFramesData: PropTypes.func,
  productDescription: PropTypes.string,
  isSubmitting: PropTypes.bool.isRequired,
  title: PropTypes.string,
  audience: PropTypes.string,
  businessGoal: PropTypes.string,
  selectedCategory: PropTypes.string,
  selectedProblemChips: PropTypes.arrayOf(PropTypes.string),
};

export default DesignSelection;
